
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { createUser } from '../../redux/createUser';
import { fetchSubadmins } from '../../redux/fetchSubadmins';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddUser = () => {
  // State for the form fields
  const [formValues, setFormValues] = useState({
    userId: '',
    userType: '',
    name: '',
    address: '',
    email: '',
    phone: '',
    password: '',
    subadminId: '',
    permission: 'view',
  });

  // State for dynamic UI based on user role
  const [availableUserTypes, setAvailableUserTypes] = useState([]);
  const [loggedInUserType, setLoggedInUserType] = useState('');

  const dispatch = useDispatch();
  const { subadmins, loading } = useSelector((state) => state.user);

  // Effect to decode JWT and set user roles on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const userRole = decodedToken.userType;
        setLoggedInUserType(userRole);

        // Set available user types based on the logged-in user's role
        if (userRole === 'Admin') {
          setAvailableUserTypes([
            { label: 'Sub Admin', value: 'Subadmin' },
            { label: 'Client Admin', value: 'ClientAdmin' },
          ]);
        } else if (userRole === 'Subadmin') {
          setAvailableUserTypes([{ label: 'Client Admin', value: 'ClientAdmin' }]);
        } else if (userRole === 'ClientAdmin') {
          setAvailableUserTypes([{ label: 'Supervisor', value: 'Supervisor' }]);
        } else {
          setAvailableUserTypes([]);
        }
      } catch (err) {
        console.error('Error decoding token:', err);
        toast.error('Invalid token. Please log in again.');
      }
    }
  }, []);

  // Effect to fetch subadmins when an Admin is creating a Client Admin
  useEffect(() => {
    if (formValues.userType === 'ClientAdmin' && loggedInUserType === 'Admin') {
      dispatch(fetchSubadmins());
    }
  }, [formValues.userType, loggedInUserType, dispatch]);

  // Handler for form input changes
  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  // Helper function to display contextual messages
  const getMessage = () => {
    if (formValues.userType === 'Subadmin') return 'This user will report to the main Admin.';
    if (formValues.userType === 'ClientAdmin') {
      return loggedInUserType === 'Admin' ? 'This user will be assigned to a Sub Admin.' : 'This user will report to you.';
    }
    if (formValues.userType === 'Supervisor') return 'This user will report to a Client Admin.';
    return '';
  };

  // Handler for form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { userId, userType, name, address, phone, password } = formValues;

    // Enhanced validation to include the conditional subadminId field
    if (!userId || !userType || !name || !address || !phone || !password || (loggedInUserType === 'Admin' && formValues.userType === 'ClientAdmin' && !formValues.subadminId)) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      // Using .unwrap() provides better promise handling for createAsyncThunk
      await dispatch(createUser(formValues)).unwrap(); 
      toast.success('User created successfully!');
      // Reset form to initial state
      setFormValues({
        userId: '', userType: '', name: '', address: '', email: '',
        phone: '', password: '', subadminId: '', permission: 'view',
      });
    } catch (error) {
      toast.error(error.message || 'Failed to create user.');
    }
  };
  
  // Style object for the gradient header to ensure it's always applied
  const headerStyle = {
    background: 'linear-gradient(45deg, #0d6efd, #0dcaf0)',
    borderTopLeftRadius: '1rem',
    borderTopRightRadius: '1rem',
  };

  return (
    // A light background for the page to make the card pop
    <div className=" min-vh-100 py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            {/* Using a larger shadow for more depth */}
            <div className="card shadow-lg" style={{ borderRadius: '1rem' }}>
              {/* Applying the header style directly to fix visibility */}
              <div className="card-header p-4 text-white text-center" style={headerStyle}>
                <h3 className="mb-0">Create a New User</h3>
                <p className="mb-0 opacity-75">Fill out the form below to add a user to the system</p>
              </div>
              <div className="card-body p-4 p-md-5">
                <form onSubmit={handleSubmit} className="row g-4">
                  
                  {/* User Type */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <select id="userType" name="userType" className="form-select" value={formValues.userType} onChange={handleChange} required>
                        <option value="" disabled>Select a Role...</option>
                        {availableUserTypes.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                      <label htmlFor="userType">User Type*</label>
                    </div>
                    {getMessage() && <div className="form-text text-muted ps-2 mt-2">{getMessage()}</div>}
                  </div>
                  
                  {/* Permission */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <select id="permission" name="permission" className="form-select" value={formValues.permission} onChange={handleChange} required>
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                      </select>
                      <label htmlFor="permission">Permission Level*</label>
                    </div>
                  </div>

                  {/* Conditional Subadmin Assignment */}
                  {loggedInUserType === 'Admin' && formValues.userType === 'ClientAdmin' && (
                    <div className="col-12">
                      <div className="form-floating">
                        <select id="subadminId" name="subadminId" className="form-select" value={formValues.subadminId} onChange={handleChange} required>
                          <option value="" disabled>Select a Sub Admin to assign...</option>
                          {loading ? (
                            <option disabled>Loading Sub Admins...</option>
                          ) : (
                            subadmins.map((sub) => <option key={sub._id} value={sub._id}>{sub.name}</option>)
                          )}
                        </select>
                        <label htmlFor="subadminId">Assign to Sub Admin*</label>
                      </div>
                    </div>
                  )}

                  {/* User ID */}
                  <div className="col-md-6">
                    <div className="input-group">
                       <span className="input-group-text"><i className="bi bi-person-badge"></i></span>
                       <div className="form-floating flex-grow-1">
                         <input type="text" id="userId" name="userId" className="form-control" value={formValues.userId} onChange={handleChange} placeholder="User ID" required />
                         <label htmlFor="userId">User ID*</label>
                       </div>
                    </div>
                  </div>
                  
                  {/* Full Name */}
                  <div className="col-md-6">
                    <div className="input-group">
                       <span className="input-group-text"><i className="bi bi-person-fill"></i></span>
                       <div className="form-floating flex-grow-1">
                         <input type="text" id="name" name="name" className="form-control" value={formValues.name} onChange={handleChange} placeholder="Full Name" required />
                         <label htmlFor="name">Full Name*</label>
                       </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text"><i className="bi bi-envelope-fill"></i></span>
                       <div className="form-floating flex-grow-1">
                         <input type="email" id="email" name="email" className="form-control" value={formValues.email} onChange={handleChange} placeholder="Email (Optional)" />
                         <label htmlFor="email">Email (Optional)</label>
                       </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text"><i className="bi bi-telephone-fill"></i></span>
                      <div className="form-floating flex-grow-1">
                        <input type="tel" id="phone" name="phone" className="form-control" value={formValues.phone} onChange={handleChange} placeholder="Phone Number" required />
                        <label htmlFor="phone">Phone Number*</label>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <div className="input-group">
                      <span className="input-group-text"><i className="bi bi-geo-alt-fill"></i></span>
                      <div className="form-floating flex-grow-1">
                        <input type="text" id="address" name="address" className="form-control" value={formValues.address} onChange={handleChange} placeholder="Full Address" required />
                        <label htmlFor="address">Full Address*</label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Password */}
                  <div className="col-md-6">
                     <div className="input-group">
                       <span className="input-group-text"><i className="bi bi-lock-fill"></i></span>
                       <div className="form-floating flex-grow-1">
                         <input type="password" id="password" name="password" className="form-control" value={formValues.password} onChange={handleChange} placeholder="Password" required />
                         <label htmlFor="password">Password*</label>
                       </div>
                     </div>
                  </div>

                  {/* Submit Button */}
                  <div className="col-12 text-center mt-5">
                    <button type="submit" className="btn btn-primary btn-lg w-50" disabled={loading} style={{ borderRadius: '50px', padding: '12px 0' }}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating User...
                        </>
                      ) : 'Create User'}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer position="bottom-right" theme="colored" autoClose={5000} hideProgressBar={false} />
      </div>
    </div>
  );
};

export default AddUser;